import React, { useContext, useState, useEffect, useMemo } from 'react';
import { CartContext } from '../../../context/CartContext';
import { Printer, X, Receipt, Percent, IndianRupee, Tag, ChevronDown, Send, Phone, User, AlertCircle, Gift, Split } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useKOT } from '../../../hooks/useKOT';
import { useMenuItems } from '../../../hooks/useMenuItems';
import { DEFAULT_PRINTER_CONFIG, PRINT_SETTINGS } from '../../../config/printerConfig';
import printService from '../../../services/printService';
import receiptConfigurationService from '../../../services/receiptConfigurationService';
import branchService from '../../../services/branchService';
import { billService } from '../../../services/billService';
import { generateBillContent, DEFAULT_RECEIPT_CONFIG } from '../../../utils/receiptFormatter';
import { useDiscountPOS, calculateDiscountAmount } from '../../../hooks/useDiscountPOS';
import { useAuth } from '../../../context/AuthContext';
import { P } from '../../../config/permissionCodes';
import whatsappService from '../../../services/whatsappService';
import AppliedDiscountBadge from './AppliedDiscountBadge';
import DiscountSelectionModal from './DiscountSelectionModal';
import SplitBillModal from './SplitBillModal';

/**
 * PrintReceiptModal - Print receipt OR send e-Bill with optional discount
 * Used for printing receipt or sending e-bill before finalizing the bill
 *
 * Can be used in multiple ways:
 * 1. From POSScreen (print): Uses CartContext, mode="print"
 * 2. From POSScreen (e-bill): Uses CartContext, mode="ebill"
 * 3. From TableSelectionPage: Pass tableId, tableSplit, tableName, pax as props
 *
 * @param {string} mode - 'print' (default) or 'ebill'
 * @param {boolean} canApplyPromo - Permission to apply promo codes
 * @param {boolean} canApplyDiscount - Permission to apply manual discounts
 */
export default function PrintReceiptModal({
  onClose,
  // Initial mode: 'print' or 'ebill' (can be toggled inside modal)
  mode: initialMode = 'print',
  // Optional props for standalone usage (from TableSelectionPage)
  tableId: propTableId,
  tableSplit: propTableSplit,
  tableName: propTableName,
  pax: propPax,
  onSuccess, // Callback when print/ebill is successful (for refreshing tables)
  // Permission props
  canApplyPromo = true,
  canApplyDiscount = true,
  canReprint = true,
}) {
  const { getCurrentOrder, currentTableId, currenttableSplit, markBillPrinted, linkedCustomer, linkedWaiter } = useContext(CartContext);
  const { hasPermission, user } = useAuth();

  // NC permission — check both dine-in and takeaway codes
  const canMarkNc = hasPermission(P.POS.DINE_IN.NC) || hasPermission(P.POS.TAKEAWAY.NC);

  // Discount threshold for the current user. The session response exposes it as
  // a top-level numeric field (e.g. 20.00 meaning 20%) alongside email/name/role.
  // AuthContext sometimes filters this field out of `user`, so we read directly
  // from the stored session payload under the 'currentUser' key in sessionStorage
  // (with localStorage as a fallback). null = no cap configured → validation
  // never fires.
  const discountThreshold = useMemo(() => {
    const readNum = (val) => {
      if (val === undefined || val === null || val === '') return null;
      const n = Number(val);
      return Number.isNaN(n) ? null : n;
    };

    // 1. Preferred: useAuth's user object (if AuthContext exposes the field)
    let val = readNum(user?.discountThreshold);
    if (val !== null) return val;

    // 2. Read 'currentUser' directly from session/local storage
    for (const storage of [sessionStorage, localStorage]) {
      try {
        const raw = storage.getItem('currentUser');
        if (!raw) continue;
        const parsed = JSON.parse(raw);
        val = readNum(parsed?.discountThreshold);
        if (val !== null) return val;
      } catch {
        // ignore parse errors and try next storage
      }
    }

    return null;
  }, [user]);

  // Internal mode state - allows toggling between print and ebill
  const [mode, setMode] = useState(initialMode);

  // Use props if provided, otherwise fall back to CartContext
  const effectiveTableId = propTableId || currentTableId;
  const effectiveTableSplit = propTableSplit || currenttableSplit || 'A';
  const currentOrder = getCurrentOrder();

  // Build table info from props or CartContext
  const tableInfo = propTableId ? {
    tableId: propTableId,
    tableName: propTableName || `Table ${propTableId}`,
    tableType: 'TABLE',
    pax: propPax || 2
  } : currentOrder;

  // Display name — replace MAVERICK_COUNTER with Takeaway
  const displayTableName = tableInfo?.tableName?.toUpperCase().includes('MAVERICK_COUNTER')
    ? 'Takeaway'
    : tableInfo?.tableName || 'N/A';

  // Get all items from active KOTs
  const { activeOrders, loading: ordersLoading } = useKOT(
    effectiveTableId,
    effectiveTableSplit,
    { skipIfNew: false }
  );

  // Get menu items to lookup tax information
  const { items: menuItems, loading: menuItemsLoading } = useMenuItems();

  // Create a lookup map for menu items by ID
  const menuItemsMap = useMemo(() => {
    const map = {};
    (menuItems || []).forEach(item => {
      map[item.id] = item;
    });
    return map;
  }, [menuItems]);

  // Extract all items from all KOTs with tax data from menu items
  const allKOTItems = useMemo(() => {
    return (activeOrders || []).flatMap(kot =>
      (kot.items || []).map(item => {
        // Look up menu item to get current effective tax rates
        const menuItem = menuItemsMap[item.menuItemId];

        return {
          cartId: `${kot.kotNumber}-${item.id}`,
          id: item.id,
          menuItemId: item.menuItemId,
          variantId: item.variantId || null,
          name: item.itemName || item.name,
          price: item.unitPrice || item.price || 0,
          finalPrice: item.unitPrice || item.price || 0,
          quantity: item.quantity || 1,
          variantName: item.variantName,
          // Tax information from menu item's effective tax
          taxPercentage: menuItem?.effectiveTaxRate || 0,
          cgstRate: menuItem?.effectiveTaxCgstRate || 0,
          sgstRate: menuItem?.effectiveTaxSgstRate || 0,
          inclusiveTax: menuItem?.effectiveInclusiveTaxRate !== false
        };
      })
    );
  }, [activeOrders, menuItemsMap]);

  // Extract all order IDs from active KOTs for bill creation
  const orderIds = (activeOrders || [])
    .map(order => order.id)
    .filter(id => id != null);

  // Group items by menuItemId + variantId for receipt display & discount calculation
  const groupedItems = useMemo(() => {
    const map = {};
    allKOTItems.forEach((item) => {
      const key = `${item.menuItemId}-${item.variantId || ''}`;
      if (!map[key]) {
        map[key] = {
          ...item,
          groupKey: key,
          quantity: 0,
          childCartIds: [],
        };
      }
      map[key].quantity += item.quantity || 1;
      map[key].childCartIds.push(item.cartId);
    });
    return Object.values(map);
  }, [allKOTItems]);

  // Flat receipt items (grouped by name) for print HTML
  const receiptItems = groupedItems.map((g) => ({
    name: g.name + (g.variantName ? ` (${g.variantName})` : ''),
    quantity: g.quantity,
    price: g.price || 0,
    total: (g.price || 0) * g.quantity,
  }));

  // Discount POS hook for code-based discounts
  const {
    suggestions: discountSuggestions,
    appliedDiscount,
    loading: discountsLoading,
    validating: codeValidating,
    error: discountError,
    fetchSuggestions,
    validateCode,
    applyDiscount,
    removeDiscount,
  } = useDiscountPOS();

  // Manual discount state (fallback)
  const [discountMode, setDiscountMode] = useState(() =>
  canApplyPromo ? 'code' : 'manual' );
  const [discountType, setDiscountType] = useState('PERCENTAGE');
  const [discountPercentage, setDiscountPercentage] = useState(0);
  const [discountFixedAmount, setDiscountFixedAmount] = useState(0);
  const [showDiscountModal, setShowDiscountModal] = useState(false);

  // Item-level NC state
  const [compItems, setCompItems] = useState({});

  // Bill-level NC state
  const [isNcBill, setIsNcBill] = useState(false);
  const [ncReason, setNcReason] = useState('');
  const NC_REASONS = [
    { code: 'STAFF_MEAL', label: 'Staff Meal' },
    { code: 'OWNER_CONSUMPTION', label: 'Owner Consumption' },
    { code: 'GUEST_COMPLAINT', label: 'Guest Complaint' },
    { code: 'FOOD_QUALITY', label: 'Food Quality Issue' },
    { code: 'WRONG_ORDER', label: 'Wrong Order' },
    { code: 'BIRTHDAY', label: 'Birthday/Anniversary' },
    { code: 'PR_MARKETING', label: 'PR / Marketing' },
    { code: 'VIP_GUEST', label: 'VIP Guest' },
    { code: 'TASTING', label: 'Menu Tasting' },
    { code: 'MGMT_DISCRETION', label: 'Management Discretion' },
  ];

  const toggleBillNc = () => {
    if (isNcBill) {
      setIsNcBill(false);
      setNcReason('');
      setCompItems({}); // Clear all comp when NC is turned off
    } else {
      setIsNcBill(true);
      // Mark all items as comp when bill-level NC
      const allComp = {};
      groupedItems.forEach(item => {
        allComp[item.groupKey] = { reason: 'NC Bill' };
      });
      setCompItems(allComp);
    }
  };

  const toggleComp = (groupKey) => {
    setCompItems((prev) => {
      const updated = { ...prev };
      if (updated[groupKey]) {
        delete updated[groupKey];
      } else {
        updated[groupKey] = { reason: NC_REASONS[0].code };
      }
      return updated;
    });
  };

  const setCompReason = (groupKey, reason) => {
    setCompItems((prev) => ({
      ...prev,
      [groupKey]: { ...prev[groupKey], reason },
    }));
  };

  // Receipt configuration state
  const [receiptConfig, setReceiptConfig] = useState(DEFAULT_RECEIPT_CONFIG);
  const [branchDetails, setBranchDetails] = useState(null);

  // Printing state
  const [isPrinting, setIsPrinting] = useState(false);
  const [showSplitBill, setShowSplitBill] = useState(false);
  const [splitResult, setSplitResult] = useState(null); // [{label, amount, items}]
  const [printingStatus, setPrintingStatus] = useState(''); // 'creating_bill', 'printing'

  // Bill number from API response (will be set after bill creation)
  const [billNumber, setBillNumber] = useState(null);

  // E-Bill specific state
  const [whatsappNumber, setWhatsappNumber] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [waStatus, setWaStatus] = useState(null); // null = loading, 'connected' | 'not_linked' | 'error'
  const [waPhone, setWaPhone] = useState(null);

  // Get customer phone from linked customer
  const customerPhone = linkedCustomer?.phone || linkedCustomer?.mobileNumber || linkedCustomer?.mobile || '';
  const customerName = linkedCustomer?.name || linkedCustomer?.customerName || '';

  // Initialize whatsapp number from customer phone when in ebill mode
  useEffect(() => {
    if (mode === 'ebill' && customerPhone && !whatsappNumber) {
      setWhatsappNumber(customerPhone.replace(/\D/g, ''));
    }
  }, [mode, customerPhone]);

  // Extract single branchId from user (branchIds may be string, array, or comma-separated)
  const branchId = (() => {
    const raw = user?.branchId;
    if (!raw) return null;
    if (Array.isArray(raw)) return raw[0] || null;
    if (typeof raw === 'string' && raw.includes(',')) return raw.split(',')[0].trim();
    return raw;
  })();

  // Check WhatsApp connection status for this branch when in e-bill mode
  useEffect(() => {
    if (mode !== 'ebill' || !branchId) return;
    let cancelled = false;

    whatsappService.getStatus(branchId)
      .then(data => {
        if (cancelled) return;
        if (data.linked) {
          setWaStatus('connected');
          setWaPhone(data.phone);
        } else {
          setWaStatus('not_linked');
        }
      })
      .catch(() => {
        if (!cancelled) setWaStatus('error');
      });

    return () => { cancelled = true; };
  }, [mode, branchId]);

  // Add this after the useDiscountPOS hook block:
useEffect(() => {
  if (!canApplyPromo && appliedDiscount) {
    removeDiscount();
  }
}, [canApplyPromo]);

  // Validate phone number (Indian format)
  const validatePhone = (phone) => {
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 10) return true;
    if (cleaned.length === 12 && cleaned.startsWith('91')) return true;
    return false;
  };

  // Handle phone input change
  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/\D/g, '');
    setWhatsappNumber(value);
    setPhoneError('');
  };

  // Theme colors based on mode
  const themeColors = mode === 'ebill' ? {
    gradient: 'from-green-600 to-green-700',
    hoverGradient: 'from-green-700 to-green-800',
    light: 'text-green-100',
    bg: 'bg-green-600',
    hoverBg: 'hover:bg-green-700',
  } : {
    gradient: 'from-teal-600 to-teal-700',
    hoverGradient: 'from-teal-700 to-teal-800',
    light: 'text-teal-100',
    bg: 'bg-teal-600',
    hoverBg: 'hover:bg-teal-700',
  };

  // Calculate tax rates from items (weighted average) and determine if inclusive
  const calculateTaxRates = () => {
    if (allKOTItems.length === 0) return { cgstRate: 0, sgstRate: 0, taxPercentage: 0, isInclusive: true };

    let totalValue = 0;
    let weightedCgst = 0;
    let weightedSgst = 0;

    allKOTItems.forEach(item => {
      const itemValue = (item.finalPrice || item.price) * item.quantity;
      totalValue += itemValue;
      weightedCgst += (item.cgstRate || 0) * itemValue;
      weightedSgst += (item.sgstRate || 0) * itemValue;
    });

    const avgCgst = totalValue > 0 ? weightedCgst / totalValue : 0;
    const avgSgst = totalValue > 0 ? weightedSgst / totalValue : 0;

    // Check if tax is inclusive (use first item's setting, default to true)
    const isInclusive = allKOTItems[0]?.inclusiveTax !== false;

    return {
      cgstRate: avgCgst,
      sgstRate: avgSgst,
      taxPercentage: avgCgst + avgSgst,
      isInclusive
    };
  };

  const calculatedTaxRates = calculateTaxRates();

  // Tax configuration - calculated from items
  const TAX_PERCENTAGE = calculatedTaxRates.taxPercentage;
  const CGST_RATE = calculatedTaxRates.cgstRate;
  const SGST_RATE = calculatedTaxRates.sgstRate;
  const IS_INCLUSIVE_TAX = calculatedTaxRates.isInclusive;

  // Calculate totals (subtotal = sum of item prices as displayed)
  const subtotal = allKOTItems.reduce((total, item) => {
    const price = item.finalPrice || item.price;
    return total + (price * item.quantity);
  }, 0);

  // Calculate comp amount (sum of comped grouped item prices)
  const compAmount = groupedItems.reduce((sum, item) => {
    if (compItems[item.groupKey]) {
      return sum + (item.price * item.quantity);
    }
    return sum;
  }, 0);

  // Effective manual discount expressed as % of subtotal (used for threshold check).
  // For PERCENTAGE mode it's the entered value; for FIXED it's converted to its
  // equivalent % so we can compare both modes against the same threshold value.
  const effectiveManualDiscountPct = (() => {
    if (discountMode !== 'manual' || !canApplyDiscount) return 0;
    if (discountType === 'PERCENTAGE') return Number(discountPercentage) || 0;
    if (!subtotal) return 0;
    return ((Number(discountFixedAmount) || 0) / subtotal) * 100;
  })();

  // True when the user's manual discount exceeds their permitted threshold.
  // When this fires: print button disables, validation message shows, and the
  // discount/tax math falls back to as-if no discount was applied.
  const exceedsDiscountThreshold =
    discountMode === 'manual'
    && canApplyDiscount
    && discountThreshold !== null
    && effectiveManualDiscountPct > discountThreshold;

  // Calculate promo/manual discount amount (use grouped items so BOGO sees correct qty)
  // Only compute if user has the relevant permission
  const promoDiscount = (() => {
    if (discountMode === 'code' && appliedDiscount && canApplyPromo) {
      const cartItems = groupedItems.map(item => ({
        menuItemId: item.menuItemId,
        categoryId: item.categoryId,
        price: item.finalPrice || item.price,
        quantity: item.quantity,
      }));
      return calculateDiscountAmount(appliedDiscount, cartItems, subtotal);
    } else if (discountMode === 'manual' && canApplyDiscount) {
      // Discount over the permitted threshold → don't apply it. The UI also
      // disables the print button and shows a validation message.
      if (exceedsDiscountThreshold) return 0;
      return discountType === 'PERCENTAGE'
        ? (subtotal * discountPercentage) / 100
        : Math.min(discountFixedAmount, subtotal);
    }
    return 0;
  })();

  // Total discount = promo discount + comp amount
  const discountAmount = promoDiscount + compAmount;

  // Calculate tax amounts based on whether tax is inclusive or exclusive
  const amountAfterDiscount = subtotal - discountAmount;

  let taxableAmount, taxAmount, cgstAmount, sgstAmount, totalAmount;

  if (IS_INCLUSIVE_TAX) {
    const totalTaxRate = TAX_PERCENTAGE;
    taxAmount = totalTaxRate > 0 ? (amountAfterDiscount * totalTaxRate) / (100 + totalTaxRate) : 0;
    taxableAmount = amountAfterDiscount - taxAmount;
    cgstAmount = CGST_RATE > 0 ? (amountAfterDiscount * CGST_RATE) / (100 + TAX_PERCENTAGE) : 0;
    sgstAmount = SGST_RATE > 0 ? (amountAfterDiscount * SGST_RATE) / (100 + TAX_PERCENTAGE) : 0;
    totalAmount = amountAfterDiscount;
  } else {
    taxableAmount = amountAfterDiscount;
    cgstAmount = (taxableAmount * CGST_RATE) / 100;
    sgstAmount = (taxableAmount * SGST_RATE) / 100;
    taxAmount = cgstAmount + sgstAmount;
    totalAmount = taxableAmount + taxAmount;
  }

  const finalAmount = Math.round(totalAmount);
  const roundOff = finalAmount - totalAmount;

  const effectiveDiscountPercentage = subtotal > 0
    ? (discountAmount / subtotal) * 100
    : 0;

  // Calculate item-level totals for bill items including CGST/SGST
  const calculateItemTotals = (item) => {
    const basePrice = item.finalPrice || item.price;
    const itemSubtotal = basePrice * item.quantity;
    const itemDiscountAmount = (itemSubtotal * effectiveDiscountPercentage) / 100;
    const itemAmountAfterDiscount = itemSubtotal - itemDiscountAmount;

    const itemCgstRate = item.cgstRate || CGST_RATE;
    const itemSgstRate = item.sgstRate || SGST_RATE;
    const itemTaxPercentage = item.taxPercentage || TAX_PERCENTAGE;
    const itemIsInclusive = item.inclusiveTax !== false;

    let itemTaxableAmount, itemCgstAmount, itemSgstAmount, itemTaxAmount, itemTotalAmount;

    if (itemIsInclusive) {
      const totalRate = itemTaxPercentage;
      itemTaxAmount = totalRate > 0 ? (itemAmountAfterDiscount * totalRate) / (100 + totalRate) : 0;
      itemTaxableAmount = itemAmountAfterDiscount - itemTaxAmount;
      itemCgstAmount = itemCgstRate > 0 ? (itemAmountAfterDiscount * itemCgstRate) / (100 + itemTaxPercentage) : 0;
      itemSgstAmount = itemSgstRate > 0 ? (itemAmountAfterDiscount * itemSgstRate) / (100 + itemTaxPercentage) : 0;
      itemTotalAmount = itemAmountAfterDiscount;
    } else {
      itemTaxableAmount = itemAmountAfterDiscount;
      itemCgstAmount = (itemTaxableAmount * itemCgstRate) / 100;
      itemSgstAmount = (itemTaxableAmount * itemSgstRate) / 100;
      itemTaxAmount = itemCgstAmount + itemSgstAmount;
      itemTotalAmount = itemTaxableAmount + itemTaxAmount;
    }

    return {
      unitPrice: basePrice,
      discountAmount: itemDiscountAmount,
      taxableAmount: itemTaxableAmount,
      taxPercentage: itemTaxPercentage,
      cgstRate: itemCgstRate,
      sgstRate: itemSgstRate,
      cgstAmount: itemCgstAmount,
      sgstAmount: itemSgstAmount,
      taxAmount: itemTaxAmount,
      totalAmount: itemTotalAmount,
      inclusiveTax: itemIsInclusive
    };
  };

  // Build a lookup from cartId → groupKey for comp propagation
  const cartIdToGroupKey = useMemo(() => {
    const map = {};
    groupedItems.forEach((g) => {
      g.childCartIds.forEach((cid) => { map[cid] = g.groupKey; });
    });
    return map;
  }, [groupedItems]);

  // Prepare bill payload for API (without payment details)
  const prepareBillPayload = () => {
    const billItemsPayload = allKOTItems.map(item => {
      const groupKey = cartIdToGroupKey[item.cartId];
      const isComped = !!compItems[groupKey];
      const itemTotals = calculateItemTotals(item);
      const compDiscountAmount = isComped
        ? parseFloat((item.price * item.quantity).toFixed(2))
        : parseFloat(itemTotals.discountAmount.toFixed(2));
      return {
        menuItemId: item.menuItemId,
        variantId: item.variantId || null,
        itemName: item.name,
        variantName: item.variantName || null,
        quantity: item.quantity,
        unitPrice: parseFloat(itemTotals.unitPrice.toFixed(2)),
        discountAmount: compDiscountAmount,
        taxPercentage: parseFloat(itemTotals.taxPercentage.toFixed(2)),
        cgstRate: parseFloat(itemTotals.cgstRate.toFixed(2)),
        sgstRate: parseFloat(itemTotals.sgstRate.toFixed(2)),
        taxableAmount: parseFloat(itemTotals.taxableAmount.toFixed(2)),
        cgstAmount: parseFloat(itemTotals.cgstAmount.toFixed(2)),
        sgstAmount: parseFloat(itemTotals.sgstAmount.toFixed(2)),
        taxAmount: parseFloat(itemTotals.taxAmount.toFixed(2)),
        totalAmount: parseFloat(itemTotals.totalAmount.toFixed(2)),
        inclusiveTaxRate: itemTotals.inclusiveTax,
        isComp: isComped,
        compReason: isComped ? (compItems[groupKey]?.reason || null) : null,
      };
    });

    // Determine discount reason based on mode
    const discountReason = discountMode === 'code' && appliedDiscount && canApplyPromo
      ? appliedDiscount.name
      : discountMode === 'manual' && discountAmount > 0 && canApplyDiscount
        ? 'Manual discount'
        : null;

    return {
      tableId: tableInfo.tableId,
      tableSplit: effectiveTableSplit,
      customerId: linkedCustomer?.id || null,
      waiterId: linkedWaiter?.id || null,
      waiterName: linkedWaiter?.name || null,
      totalItems: allKOTItems.length,
      subtotal: parseFloat(subtotal.toFixed(2)),
      discountId: (canApplyPromo && appliedDiscount?.id) || null,
      discountCode: (canApplyPromo && appliedDiscount?.code) || null,
      discountPercentage: parseFloat(effectiveDiscountPercentage.toFixed(2)),
      discountAmount: parseFloat(discountAmount.toFixed(2)),
      discountReason: discountReason,
      taxPercentage: parseFloat(TAX_PERCENTAGE.toFixed(2)),
      cgstRate: parseFloat(CGST_RATE.toFixed(2)),
      sgstRate: parseFloat(SGST_RATE.toFixed(2)),
      taxableAmount: parseFloat(taxableAmount.toFixed(2)),
      cgstAmount: parseFloat(cgstAmount.toFixed(2)),
      sgstAmount: parseFloat(sgstAmount.toFixed(2)),
      taxAmount: parseFloat(taxAmount.toFixed(2)),
      totalAmount: parseFloat(totalAmount.toFixed(2)),
      roundOff: parseFloat(roundOff.toFixed(2)),
      finalAmount: parseFloat(finalAmount.toFixed(2)),
      inclusiveTaxRate: IS_INCLUSIVE_TAX,
      paymentStatus: 'pending',
      paidAmount: 0,
      balanceDue: parseFloat(finalAmount.toFixed(2)),
      billStatus: 'payment_pending',
      billType: isNcBill ? 'nc' : (Object.keys(compItems).length > 0 ? 'partial_nc' : 'sale'),
      isNc: isNcBill,
      ncReason: isNcBill ? ncReason : null,
      ncAmount: parseFloat(compAmount.toFixed(2)),
      billDeliveryMethod: mode === 'ebill' ? 'WHATSAPP' : 'PRINT',
      recipientPhone: mode === 'ebill' && whatsappNumber
        ? (whatsappNumber.startsWith('91') ? whatsappNumber : '91' + whatsappNumber)
        : null,
      referenceBillNumber: null,
      notes: null,
      billItems: billItemsPayload,
      payments: [],
      orderIds: orderIds
    };
  };

  // Fetch receipt configuration and branch details
  useEffect(() => {
    const fetchReceiptConfig = async () => {
      try {
        const config = await receiptConfigurationService.getDefault('BILL');
        if (config) {
          setReceiptConfig(config);
        }
      } catch (error) {
        console.warn('Failed to fetch receipt configuration, using defaults:', error);
      }
    };

    const fetchBranchDetails = async () => {
      try {
        const branch = await branchService.getCurrentBranch();
        if (branch) {
          setBranchDetails(branchService.formatBranchForReceipt(branch));
        }
      } catch (error) {
        console.warn('Failed to fetch branch details:', error);
      }
    };

    fetchReceiptConfig();
    fetchBranchDetails();
  }, []);

  // Handler to open discount modal with lazy loading
  const handleOpenDiscountModal = () => {
    setShowDiscountModal(true);
    const itemIds = [...new Set(allKOTItems.map(item => item.menuItemId).filter(Boolean))];
    fetchSuggestions({
      orderType: tableInfo?.tableType === 'TABLE' ? 'dine_in' : 'takeaway',
      subtotal,
      itemIds,
      categoryIds: [],
      customerId: null,
    });
  };

  const timestamp = new Date().toLocaleString('en-IN', {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });

  const PRINTER_CONFIG = DEFAULT_PRINTER_CONFIG;

  // Browser print fallback
  const handleThermalPrint = (invoiceNumber) => {
    const printWindow = window.open('', '', 'width=800,height=600');

    if (!printWindow) {
      toast.error('Popup blocked! Please allow popups for this site.');
      return;
    }

    const receiptHTML = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="UTF-8">
          <title>Receipt - ${invoiceNumber}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }

            @media screen {
              body {
                font-family: 'Courier New', Courier, monospace;
                background: #f5f5f5;
                padding: 20px;
                display: flex;
                justify-content: center;
              }
              .receipt-container {
                background: white;
                width: ${PRINTER_CONFIG.width};
                padding: ${PRINTER_CONFIG.padding};
                box-shadow: 0 2px 10px rgba(0,0,0,0.1);
                border: 1px solid #ddd;
              }
            }

            @media print {
              @page { size: ${PRINTER_CONFIG.width} auto; margin: 0; }
              body { margin: 0; padding: 0; width: ${PRINTER_CONFIG.width}; }
              .receipt-container {
                width: 100%;
                padding: ${PRINTER_CONFIG.padding};
                ${PRINT_SETTINGS.enableAutoCut ? `padding-bottom: ${PRINT_SETTINGS.bottomMargin};` : ''}
              }
            }

            .receipt-container {
              font-family: 'Courier New', Courier, monospace;
              font-size: ${PRINTER_CONFIG.fontSize};
              line-height: ${PRINTER_CONFIG.lineHeight};
              color: #000;
            }

            .text-center { text-align: center; }
            .text-right { text-align: right; }
            .font-bold { font-weight: bold; }
            .text-xs { font-size: 0.8em; }
            .text-sm { font-size: 0.9em; }
            .mb-2 { margin-bottom: 0.5em; }
            .mb-4 { margin-bottom: 1em; }
            .mt-2 { margin-top: 0.5em; }
            .pb-2 { padding-bottom: 0.5em; }
            .pt-2 { padding-top: 0.5em; }

            .header {
              text-align: center;
              margin-bottom: 1em;
              padding-bottom: 1em;
              border-bottom: 2px dashed #000;
            }

            .company-name { font-size: 1.4em; font-weight: bold; margin-bottom: 0.25em; }

            .info-section {
              margin-bottom: 1em;
              padding-bottom: 0.75em;
              border-bottom: 1px solid #000;
            }

            .info-row {
              display: flex;
              justify-content: space-between;
              margin-bottom: 0.25em;
              font-size: 0.9em;
            }

            .items-header {
              display: flex;
              justify-content: space-between;
              font-weight: bold;
              margin-bottom: 0.5em;
              padding-bottom: 0.5em;
              border-bottom: 1px solid #000;
              font-size: 0.9em;
            }

            .items-header span:nth-child(1) { flex: 2; }
            .items-header span:nth-child(2) { width: 2.5em; text-align: center; }
            .items-header span:nth-child(3) { width: 5em; text-align: right; }

            .item-row { margin-bottom: 0.75em; }

            .item-main {
              display: flex;
              justify-content: space-between;
              margin-bottom: 0.2em;
            }

            .item-main span:nth-child(1) { flex: 2; }
            .item-main span:nth-child(2) { width: 2.5em; text-align: center; font-weight: bold; }
            .item-main span:nth-child(3) { width: 5em; text-align: right; font-weight: bold; }

            .item-price { font-size: 0.8em; color: #333; margin-left: 0.5em; }

            .totals-section {
              border-top: 1px solid #000;
              padding-top: 0.75em;
              margin-top: 1em;
              margin-bottom: 1em;
            }

            .total-row {
              display: flex;
              justify-content: space-between;
              margin-bottom: 0.4em;
              font-size: 0.9em;
            }

            .discount-row { color: #2d7a2d; }

            .final-total {
              border-top: 2px dashed #000;
              padding-top: 0.75em;
              margin-top: 0.75em;
            }

            .final-total-row {
              display: flex;
              justify-content: space-between;
              font-weight: bold;
              font-size: 1.1em;
              padding: 0.5em;
              background: #f0f0f0;
              border-radius: 3px;
            }

            .footer {
              text-align: center;
              margin-top: 1.5em;
              padding-top: 1em;
              border-top: 2px dashed #000;
              font-size: 0.8em;
            }
          </style>
        </head>
        <body>
          <div class="receipt-container">
            <div class="header">
              ${receiptConfig.showLogo && branchDetails?.logoUrl ? `<img src="${branchDetails.logoUrl}" alt="Logo" style="max-height: 60px; max-width: 150px; margin: 0 auto 0.5em;" />` : ''}
              ${receiptConfig.showRestaurantName ? `<div class="company-name">${branchDetails?.name || 'RESTAURANT'}</div>` : ''}
              ${receiptConfig.showAddress && branchDetails?.address ? `<div class="text-xs">${branchDetails.address}</div>` : ''}
              ${receiptConfig.showPhone && branchDetails?.phone ? `<div class="text-xs">Ph: ${branchDetails.phone}</div>` : ''}
              ${receiptConfig.showGstin && branchDetails?.gstNumber ? `<div class="text-xs">GSTIN: ${branchDetails.gstNumber}</div>` : ''}
              <div class="text-xs font-bold" style="margin-top: 0.5em;">Bill #${invoiceNumber}</div>
            </div>

            <div class="info-section">
              <div class="info-row">
                <span class="font-bold">${tableInfo?.tableType === 'TABLE' ? 'Table' : 'Parcel'}:</span>
                <span class="font-bold">${displayTableName || 'N/A'}</span>
              </div>
              <div class="info-row text-xs">
                <span>Date/Time:</span>
                <span>${timestamp}</span>
              </div>
              ${tableInfo?.pax ? `<div class="info-row"><span>Guests:</span><span>${tableInfo.pax}</span></div>` : ''}
            </div>

            <div class="items-header">
              <span>Item</span>
              <span>Qty</span>
              <span>Amount</span>
            </div>

            ${receiptItems.map(item => `
              <div class="item-row">
                <div class="item-main">
                  <span>${item.name}</span>
                  <span>${item.quantity}</span>
                  <span>₹${item.total.toFixed(2)}</span>
                </div>
                <div class="item-price">@ ₹${item.price.toFixed(2)}</div>
              </div>
            `).join('')}

            <div class="totals-section">
              <div class="total-row">
                <span>Subtotal:</span>
                <span>₹${subtotal.toFixed(2)}</span>
              </div>
              ${discountAmount > 0 ? `
              <div class="total-row discount-row">
                <span>Discount ${appliedDiscount?.code ? `(${appliedDiscount.code})` : discountType === 'PERCENTAGE' ? `(${discountPercentage}%)` : ''}:</span>
                <span>- ₹${discountAmount.toFixed(2)}</span>
              </div>
              ` : ''}
              ${(CGST_RATE > 0 || SGST_RATE > 0) ? `
              <div class="total-row">
                <span>Taxable Amount:</span>
                <span>₹${taxableAmount.toFixed(2)}</span>
              </div>
              <div class="total-row">
                <span>CGST (${CGST_RATE.toFixed(1)}%):</span>
                <span>₹${cgstAmount.toFixed(2)}</span>
              </div>
              <div class="total-row">
                <span>SGST (${SGST_RATE.toFixed(1)}%):</span>
                <span>₹${sgstAmount.toFixed(2)}</span>
              </div>
              ${IS_INCLUSIVE_TAX ? `<div class="total-row text-xs" style="font-style: italic; color: #666;">(Tax included in price)</div>` : ''}
              ` : ''}
              <div class="total-row">
                <span>Round Off:</span>
                <span>${roundOff >= 0 ? '+' : ''}₹${roundOff.toFixed(2)}</span>
              </div>
            </div>

            <div class="final-total">
              <div class="final-total-row">
                <span>TOTAL:</span>
                <span>₹${finalAmount.toFixed(2)}</span>
              </div>
            </div>

            <div class="footer">
              <div class="font-bold mb-2">${receiptConfig.thankYouText || branchDetails?.thankYouMessage || 'Thank you! Visit Again!'}</div>
              <div class="text-xs">${receiptConfig?.printerPoweredBy || 'A Product by Maverick Ignite'}</div>
            </div>
          </div>

          <script>
            window.onload = function() {
              setTimeout(function() { window.print(); }, ${PRINT_SETTINGS.autoPrintDelay});
            };
            ${PRINT_SETTINGS.autoCloseAfterPrint ? `
            window.onafterprint = function() {
              setTimeout(function() { window.close(); }, ${PRINT_SETTINGS.autoCloseDelay});
            };
            ` : ''}
          </script>
        </body>
      </html>
    `;

    printWindow.document.write(receiptHTML);
    printWindow.document.close();
  };

  // Print a split receipt for one person
  const handleSplitPrint = async (invoiceNumber, split, splitIndex, totalSplits) => {
    try {
      // Filter items for this person (if items assigned), or use all items with proportional amount
      const splitItems = split.items && split.items.length > 0
        ? split.items.map(item => ({
            name: item.name,
            quantity: item.quantity,
            price: item.price,
            total: item.total,
          }))
        : receiptItems; // equal/custom split — show all items

      const splitSubtotal = split.amount;
      const proportionRatio = splitSubtotal / (finalAmount || 1);

      const billData = {
        restaurantName: branchDetails?.name || 'RESTAURANT',
        address: branchDetails?.address || null,
        phone: branchDetails?.phone || null,
        gstin: branchDetails?.gstNumber || null,
        fssai: branchDetails?.fssaiNumber || null,
        logoUrl: branchDetails?.logoUrl || null,
        merchantUpiId: branchDetails?.upiId || null,
        billNumber: invoiceNumber,
        receiptNumber: invoiceNumber,
        tableName: displayTableName,
        tableSplit: effectiveTableSplit,
        pax: tableInfo?.pax,
        waiterName: linkedWaiter?.name || null,
        customerName: linkedCustomer?.name || null,
        customerPhone: linkedCustomer?.phone || linkedCustomer?.mobileNumber || null,
        customerEmail: linkedCustomer?.email || null,
        customerAddress: linkedCustomer?.address || null,
        timestamp: new Date().toISOString(),
        orderType: tableInfo?.tableType === 'TABLE' ? 'Dine In' : 'Takeaway',
        splitLabel: `${split.label} (${splitIndex} of ${totalSplits})`,
        items: splitItems,
        subtotal: splitSubtotal,
        discountAmount: 0,
        discountPercentage: 0,
        taxAmount: split.items?.length > 0 ? (taxAmount * proportionRatio) : (taxAmount * proportionRatio),
        taxPercentage: TAX_PERCENTAGE,
        cgstRate: CGST_RATE,
        sgstRate: SGST_RATE,
        cgstAmount: cgstAmount * proportionRatio,
        sgstAmount: sgstAmount * proportionRatio,
        roundOff: 0,
        finalAmount: splitSubtotal,
      };

      const receiptOutput = generateBillContent(receiptConfig, billData);

      if (receiptOutput?.content) {
        const isPrintAgentAvailable = await printService.isPrintAgentAvailable();
        if (isPrintAgentAvailable) {
          await fetch(`http://localhost:${printService.getPrintAgentPort()}/print/bill`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              content: receiptOutput.content,
              printerId: null,
              logo: receiptOutput.logo,
              qrCode: receiptOutput.qrCode,
            })
          });
        } else {
          await printService.testPrintTemplate(
            receiptOutput.content, 'BILL', null,
            { logo: receiptOutput.logo, qrCode: receiptOutput.qrCode }
          );
        }
      }
    } catch (error) {
      console.error(`Split print error for ${split.label}:`, error);
    }
  };

  // Direct print to thermal printer
  const handleDirectPrint = async (invoiceNumber) => {
    try {
      const billData = {
        restaurantName: branchDetails?.name || 'RESTAURANT',
        address: branchDetails?.address || null,
        phone: branchDetails?.phone || null,
        gstin: branchDetails?.gstNumber || null,
        fssai: branchDetails?.fssaiNumber || null,
        logoUrl: branchDetails?.logoUrl || null,
        merchantUpiId: branchDetails?.upiId || null,
        billNumber: invoiceNumber,
        receiptNumber: invoiceNumber,
        tableName: displayTableName || 'N/A',
        tableType: tableInfo?.tableType || 'TABLE',
        tableSplit: effectiveTableSplit,
        pax: tableInfo?.pax,
        waiterName: linkedWaiter?.name || null,
        customerName: linkedCustomer?.name || null,
        customerPhone: linkedCustomer?.phone || linkedCustomer?.mobileNumber || null,
        customerEmail: linkedCustomer?.email || null,
        customerAddress: linkedCustomer?.address || null,
        // Raw ISO — the formatter parses and localises it (a pre-localised
        // string would parse as Invalid Date)
        timestamp: new Date().toISOString(),
        orderType: tableInfo?.tableType === 'TABLE' ? 'Dine In' : 'Takeaway',
        items: receiptItems.map(item => ({
          name: item.name,
          quantity: item.quantity,
          price: item.price,
          total: item.total,
        })),
        subtotal,
        discountAmount,
        discountPercentage: effectiveDiscountPercentage,
        discountType,
        discountCode: appliedDiscount?.code || null,
        discountName: appliedDiscount?.name || null,
        taxableAmount,
        taxAmount,
        taxPercentage: TAX_PERCENTAGE,
        cgstRate: CGST_RATE,
        sgstRate: SGST_RATE,
        cgstAmount,
        sgstAmount,
        inclusiveTax: IS_INCLUSIVE_TAX,
        roundOff,
        finalAmount,
      };

      const receiptOutput = generateBillContent(receiptConfig, billData);
      const response = await printService.testPrintTemplate(
        receiptOutput.content,
        'BILL',
        null,
        { logo: receiptOutput.logo, qrCode: receiptOutput.qrCode }
      );

      if (response?.success) {
        toast.success('Receipt printed successfully!', { duration: 2000 });
        return true;
      } else {
        console.warn('Direct print failed, using browser print:', response?.message);
        handleThermalPrint(invoiceNumber);
        return false;
      }
    } catch (error) {
      console.error('Direct print error:', error);
      handleThermalPrint(invoiceNumber);
      return false;
    }
  };

  // Handle print/e-bill button click - Create bill then print or send
  const handlePrint = async () => {
    if (mode === 'ebill') {
      if (!validatePhone(whatsappNumber)) {
        setPhoneError('Please enter a valid 10-digit mobile number');
        return;
      }
    }

    setIsPrinting(true);
    setPrintingStatus('creating_bill');

    try {
      // ─── SPLIT BILL: Create separate bills per check ───
      if (splitResult && splitResult.length > 0) {
        const splitGroup = crypto.randomUUID ? crypto.randomUUID() : `split-${Date.now()}`;
        const billNumbers = [];

        for (let i = 0; i < splitResult.length; i++) {
          const split = splitResult[i];
          const splitPayload = prepareBillPayload();

          // Override bill items with this check's items (for by-item split)
          if (split.items && split.items.length > 0) {
            splitPayload.billItems = split.items.map(item => ({
              menuItemId: item.menuItemId, variantId: item.variantId || null,
              itemName: item.name, variantName: item.variantName || null,
              quantity: item.quantity, unitPrice: parseFloat(item.price?.toFixed(2) || 0),
              totalAmount: parseFloat(item.total?.toFixed(2) || 0),
              discountAmount: 0, taxPercentage: parseFloat(TAX_PERCENTAGE.toFixed(2)),
              cgstRate: parseFloat(CGST_RATE.toFixed(2)), sgstRate: parseFloat(SGST_RATE.toFixed(2)),
              taxableAmount: parseFloat(item.total?.toFixed(2) || 0),
              cgstAmount: parseFloat(((item.total || 0) * CGST_RATE / 100).toFixed(2)),
              sgstAmount: parseFloat(((item.total || 0) * SGST_RATE / 100).toFixed(2)),
              taxAmount: parseFloat(((item.total || 0) * TAX_PERCENTAGE / 100).toFixed(2)),
              isComp: false, compReason: null,
            }));
            splitPayload.subtotal = parseFloat(split.subtotal?.toFixed(2) || split.amount);
            splitPayload.finalAmount = split.amount;
            splitPayload.totalAmount = split.amount;
            splitPayload.taxAmount = parseFloat((split.tax || 0).toFixed(2));
          } else {
            // Equal/custom split — keep all items but adjust amounts
            splitPayload.finalAmount = split.amount;
            splitPayload.totalAmount = split.amount;
          }

          // Tag with split group
          splitPayload.splitGroup = splitGroup;
          splitPayload.splitIndex = i + 1;
          splitPayload.splitTotal = splitResult.length;

          const response = await billService.createBill(splitPayload);
          if (response?.billNumber) {
            billNumbers.push(response.billNumber);

            // Print this check's receipt
            if (mode !== 'ebill') {
              setPrintingStatus('printing');
              await handleSplitPrint(response.billNumber, split, i + 1, splitResult.length);
            }
          }
        }

        toast.success(`${billNumbers.length} split bills created: ${billNumbers.join(', ')}`, { duration: 4000 });
        markBillPrinted();
        onClose();
        if (onSuccess) onSuccess();
        return;
      }

      // ─── NORMAL (non-split) BILL ───
      const billPayload = prepareBillPayload();
      const response = await billService.createBill(billPayload);

      if (response?.billNumber) {
        const invoiceNumber = response.billNumber;
        setBillNumber(invoiceNumber);

        if (mode === 'ebill') {
          setPrintingStatus('sending');
          toast.success(`Bill #${invoiceNumber} — e-Bill sent via WhatsApp!`, {
            duration: 3000,
            icon: '✅'
          });
        } else {
          setPrintingStatus('printing');
          await handleDirectPrint(invoiceNumber);
          toast.success(`Bill #${invoiceNumber} created and printed!`, { duration: 2000 });
        }

        markBillPrinted();
        onClose();

        if (onSuccess) {
          onSuccess();
        }
      } else {
        toast.error('Failed to get bill number from response');
        console.error('Unexpected response structure:', response);
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to create bill';
      toast.error(errorMessage, { duration: 4000 });
      console.error('Bill creation error:', error);
    } finally {
      setIsPrinting(false);
      setPrintingStatus('');
    }
  };

  // Quick discount presets
  const discountPresets = [5, 10, 15, 20];

  // Whether the discount section should be shown at all
  const showDiscountSection = canApplyPromo || canApplyDiscount;

  // Combined disabled flag for the print/send button so the threshold check
  // gates both the className styling and the disabled attribute consistently.
  const isPrintDisabled =
    isPrinting
    || receiptItems.length === 0
    || (mode === 'ebill' && whatsappNumber.length < 10)
    || exceedsDiscountThreshold;

  // Guard clause - need table info to proceed
  if (!tableInfo) return null;

  if (ordersLoading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[70] p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-8 text-center">
          <div className={`animate-spin rounded-full h-12 w-12 border-b-2 ${mode === 'ebill' ? 'border-green-600' : 'border-teal-600'} mx-auto mb-4`}></div>
          <p className="text-slate-600">Loading order details...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[70] p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-hidden flex flex-col">

        {/* Header - Theme based on mode */}
        <div className={`bg-gradient-to-r ${themeColors.gradient} text-white px-4 py-3 flex items-center gap-3 flex-shrink-0`}>
          <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
            {mode === 'ebill' ? <Send size={20} /> : <Printer size={20} />}
          </div>

          <div className="flex-1 min-w-0">
            <h2 className="text-base font-bold truncate">{tableInfo.tableName}</h2>
            <p className={`text-xs ${themeColors.light}`}>{receiptItems.length} items • ₹{finalAmount.toFixed(0)}</p>
          </div>

          {/* Mode Toggle */}
          <div className="flex bg-white/20 rounded-lg p-0.5">
            <button
              onClick={() => setMode('print')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${
                mode === 'print'
                  ? 'bg-white text-slate-800 shadow-sm'
                  : 'text-white/80 hover:text-white hover:bg-white/10'
              }`}
            >
              <Printer size={14} />
              Print
            </button>
            <button
              onClick={() => setMode('ebill')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${
                mode === 'ebill'
                  ? 'bg-white text-slate-800 shadow-sm'
                  : 'text-white/80 hover:text-white hover:bg-white/10'
              }`}
            >
              <Send size={14} />
              e-Bill
            </button>
          </div>

          <button
            onClick={onClose}
            className="p-1.5 hover:bg-white/20 rounded-lg transition flex-shrink-0"
          >
            <X size={18} />
          </button>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">

          {/* E-Bill: WhatsApp Connection Status */}
          {mode === 'ebill' && waStatus === 'connected' && (
            <div className="flex items-center gap-2 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-lg">
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse flex-shrink-0" />
              <span className="text-xs text-emerald-700">
                WhatsApp linked — sending from <span className="font-semibold">{waPhone ? `+${waPhone}` : 'your number'}</span>
              </span>
            </div>
          )}

          {mode === 'ebill' && (waStatus === 'not_linked' || waStatus === 'error') && (
            <div className="flex items-start gap-2.5 px-3 py-2.5 bg-amber-50 border border-amber-200 rounded-lg">
              <AlertCircle size={16} className="text-amber-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-xs font-medium text-amber-800">WhatsApp not linked for this branch</p>
                <p className="text-[11px] text-amber-600 mt-0.5">
                  The bill will be created, but the WhatsApp message may not be delivered.
                  Go to <span className="font-medium">Integrations → WhatsApp</span> to link your number.
                </p>
              </div>
            </div>
          )}

          {/* E-Bill: Send To Section */}
          {mode === 'ebill' && (
            <div className="bg-green-50 rounded-lg border border-green-200 overflow-hidden">
              <div className="flex items-center gap-3 px-3 py-2">
                {linkedCustomer && customerName && (
                  <div className="flex items-center gap-2 px-2 py-1 bg-green-100 rounded-full">
                    <User size={12} className="text-green-600" />
                    <span className="text-xs font-medium text-green-800">{customerName}</span>
                  </div>
                )}

                <div className="flex-1 flex items-center">
                  <div className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-l border border-r-0 text-sm font-semibold ${
                    phoneError ? 'border-red-300 bg-red-50 text-red-600' : 'border-green-300 bg-green-100 text-green-800'
                  }`}>
                    <Phone size={13} />
                    <span>+91</span>
                  </div>
                  <input
                    type="tel"
                    value={whatsappNumber}
                    onChange={handlePhoneChange}
                    placeholder="10-digit mobile number"
                    maxLength={10}
                    className={`flex-1 px-2.5 py-1.5 border rounded-r text-sm font-medium focus:outline-none focus:ring-1 focus:ring-green-500 ${
                      phoneError ? 'border-red-300 bg-red-50' : 'border-green-300 bg-white'
                    }`}
                  />
                </div>
              </div>
              {phoneError && (
                <div className="px-3 pb-2">
                  <p className="text-red-600 text-xs flex items-center gap-1">
                    <AlertCircle size={11} />
                    {phoneError}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Discount Section — only shown if user has at least one discount permission */}
          {showDiscountSection && (
            <div className="bg-teal-50 rounded-lg border border-teal-200 overflow-hidden">
              {/* Header with Mode Toggle */}
              <div className="flex items-center justify-between px-3 py-2 bg-teal-100 border-b border-teal-200">
                <div className="flex items-center gap-2">
                  <Tag size={14} className="text-teal-700" />
                  <span className="text-xs font-semibold text-teal-800">Discount</span>
                </div>
                {/* Only show mode toggle if BOTH permissions exist */}
                {canApplyPromo && canApplyDiscount && (
                  <div className="flex bg-white rounded border border-teal-300 overflow-hidden text-[10px]">
                    <button
                      onClick={() => { setDiscountMode('code'); removeDiscount(); setDiscountPercentage(0); setDiscountFixedAmount(0); }}
                      className={`px-2 py-1 font-medium transition ${
                        discountMode === 'code' ? 'bg-teal-600 text-white' : 'text-slate-500 hover:bg-teal-50'
                      }`}
                    >
                      Promo Code
                    </button>
                    <button
                      onClick={() => { setDiscountMode('manual'); removeDiscount(); }}
                      className={`px-2 py-1 font-medium transition ${
                        discountMode === 'manual' ? 'bg-teal-600 text-white' : 'text-slate-500 hover:bg-teal-50'
                      }`}
                    >
                      Manual
                    </button>
                  </div>
                )}
                {/* If only one permission, show a static label */}
                {canApplyPromo && !canApplyDiscount && (
                  <span className="text-[10px] text-teal-600 font-medium">Promo Code</span>
                )}
                {!canApplyPromo && canApplyDiscount && (
                  <span className="text-[10px] text-teal-600 font-medium">Manual</span>
                )}
              </div>

              <div className="p-3">
                {/* Code-Based Discount Mode — only if canApplyPromo */}
                {(discountMode === 'code' || (!canApplyDiscount && canApplyPromo)) && canApplyPromo && (
                  <>
                    {appliedDiscount ? (
                      <AppliedDiscountBadge
                        discount={appliedDiscount}
                        discountAmount={discountAmount}
                        onRemove={removeDiscount}
                      />
                    ) : (
                      <button
                        onClick={handleOpenDiscountModal}
                        className="w-full flex items-center justify-between p-3 bg-white border-2 border-dashed border-teal-300 rounded-lg hover:border-teal-500 hover:bg-teal-50 transition-all group"
                      >
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center group-hover:bg-teal-200 transition">
                            <Tag size={16} className="text-teal-600" />
                          </div>
                          <div className="text-left">
                            <p className="text-sm font-semibold text-slate-700">Apply Promo Code</p>
                            <p className="text-xs text-slate-500">Browse available offers</p>
                          </div>
                        </div>
                        <ChevronDown size={18} className="text-teal-500" />
                      </button>
                    )}
                  </>
                )}

                {/* Manual Discount Mode — only if canApplyDiscount */}
                {(discountMode === 'manual' || (!canApplyPromo && canApplyDiscount)) && canApplyDiscount && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <div className="flex bg-white rounded border border-teal-300 overflow-hidden flex-shrink-0">
                        <button
                          onClick={() => setDiscountType('PERCENTAGE')}
                          className={`px-2 py-1 transition ${
                            discountType === 'PERCENTAGE'
                              ? 'bg-teal-600 text-white'
                              : 'text-slate-500 hover:bg-teal-100'
                          }`}
                        >
                          <Percent size={14} />
                        </button>
                        <button
                          onClick={() => setDiscountType('FIXED')}
                          className={`px-2 py-1 transition ${
                            discountType === 'FIXED'
                              ? 'bg-teal-600 text-white'
                              : 'text-slate-500 hover:bg-teal-100'
                          }`}
                        >
                          <IndianRupee size={14} />
                        </button>
                      </div>

                      <div className="relative flex-1">
                        {discountType === 'PERCENTAGE' ? (
                          <>
                            <input
                              type="number"
                              min="0"
                              max="100"
                              value={discountPercentage || ''}
                              onChange={(e) => setDiscountPercentage(Math.min(100, Math.max(0, parseFloat(e.target.value) || 0)))}
                              placeholder="0"
                              className={`w-full px-2 py-1 pr-6 border rounded text-sm font-semibold focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-transparent bg-white ${
                                exceedsDiscountThreshold ? 'border-red-400 bg-red-50' : 'border-teal-300'
                              }`}
                            />
                            <span className={`absolute right-2 top-1/2 -translate-y-1/2 font-bold text-xs ${
                              exceedsDiscountThreshold ? 'text-red-600' : 'text-teal-600'
                            }`}>%</span>
                          </>
                        ) : (
                          <>
                            <span className={`absolute left-2 top-1/2 -translate-y-1/2 font-bold text-xs ${
                              exceedsDiscountThreshold ? 'text-red-600' : 'text-teal-600'
                            }`}>₹</span>
                            <input
                              type="number"
                              min="0"
                              max={subtotal}
                              value={discountFixedAmount || ''}
                              onChange={(e) => setDiscountFixedAmount(Math.min(subtotal, Math.max(0, parseFloat(e.target.value) || 0)))}
                              placeholder="0"
                              className={`w-full px-2 py-1 pl-5 border rounded text-sm font-semibold focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-transparent bg-white ${
                                exceedsDiscountThreshold ? 'border-red-400 bg-red-50' : 'border-teal-300'
                              }`}
                            />
                          </>
                        )}
                      </div>

                      {discountType === 'PERCENTAGE' && (
                        <div className="flex gap-0.5 flex-shrink-0">
                          {discountPresets.map((preset) => (
                            <button
                              key={preset}
                              onClick={() => setDiscountPercentage(preset)}
                              className={`px-1.5 py-1 text-[10px] font-bold rounded transition ${
                                discountPercentage === preset
                                  ? 'bg-teal-600 text-white'
                                  : 'bg-white text-teal-700 border border-teal-300 hover:bg-teal-100'
                              }`}
                            >
                              {preset}%
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Threshold-exceeded validation message */}
                    {exceedsDiscountThreshold && (
                      <div className="flex items-start gap-1.5 px-2 py-1.5 bg-red-50 border border-red-300 rounded">
                        <AlertCircle size={12} className="text-red-600 mt-0.5 flex-shrink-0" />
                        <p className="text-[11px] font-medium text-red-700 leading-snug">
                          You can apply a maximum
                          <span className="font-bold"> {discountThreshold} %</span>.
                           discount
                        </p>
                      </div>
                    )}

                    {discountAmount > 0 && !exceedsDiscountThreshold && (
                      <div className="flex items-center justify-between bg-amber-100 px-2 py-1 rounded border border-amber-300">
                        <span className="text-[10px] font-semibold text-amber-700">Manual Discount</span>
                        <span className="text-xs font-bold text-amber-700">- ₹{discountAmount.toFixed(2)}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* NC Bill Banner */}
          {isNcBill && (
            <div className="bg-amber-500 text-white px-4 py-2.5 rounded-lg flex items-center gap-2 text-sm font-semibold">
              <span className="text-lg">⚠</span>
              <span>NON-CHARGEABLE BILL{ncReason ? ` — ${NC_REASONS.find(r => r.code === ncReason)?.label || ncReason}` : ''}</span>
              {compAmount > 0 && (
                <span className="ml-auto bg-white/20 px-2 py-0.5 rounded text-xs">NC Value: ₹{compAmount.toFixed(0)}</span>
              )}
            </div>
          )}

          {/* Receipt Display */}
          <div className="bg-white p-5 rounded-lg font-mono text-sm border border-slate-200 shadow-md">
            {/* Header Section */}
            <div className="text-center border-b-2 border-dashed border-slate-300 pb-4 mb-4">
              {receiptConfig.showLogo && branchDetails?.logoUrl && (
                <img src={branchDetails.logoUrl} alt="Logo" className="mx-auto mb-2" style={{ maxHeight: '50px', maxWidth: '120px' }} />
              )}
              {receiptConfig.showRestaurantName && (
                <h1 className="text-xl font-bold text-slate-900">{branchDetails?.name || 'RESTAURANT'}</h1>
              )}
              {receiptConfig.showAddress && branchDetails?.address && (
                <p className="text-xs text-slate-600">{branchDetails.address}</p>
              )}
              {receiptConfig.showPhone && branchDetails?.phone && (
                <p className="text-xs text-slate-600">Ph: {branchDetails.phone}</p>
              )}
              {receiptConfig.showGstin && branchDetails?.gstNumber && (
                <p className="text-xs text-slate-500">GSTIN: {branchDetails.gstNumber}</p>
              )}
              {receiptConfig.showFssai && branchDetails?.fssaiNumber && (
                <p className="text-xs text-slate-500">FSSAI: {branchDetails.fssaiNumber}</p>
              )}
              {receiptConfig.headerText && (
                <p className="text-xs text-slate-600 mt-1">{receiptConfig.headerText}</p>
              )}
              <p className="text-xs text-slate-600 mt-2">
                Bill #{billNumber || '(will be generated)'}
              </p>
            </div>

            {/* Table Info */}
            <div className="border-b border-slate-300 pb-3 mb-3">
              <div className="flex justify-between text-xs mb-1">
                <span className="font-semibold text-slate-900">
                  {tableInfo.tableType === 'TABLE' ? 'Table' : 'Parcel'}:
                </span>
                <span className="font-bold text-slate-900">{tableInfo.tableName}</span>
              </div>
              <div className="flex justify-between text-xs text-slate-600">
                <span>Date/Time:</span>
                <span>{timestamp}</span>
              </div>
            </div>

            {/* Items Section */}
            <div className="mb-4">
              <div className="flex items-center justify-between text-xs font-bold text-slate-900 mb-2 pb-2 border-b border-slate-300">
                <span className="flex items-center gap-2">
                  Item
                  {canMarkNc && (
                    <button
                      onClick={toggleBillNc}
                      className={`text-[10px] px-2 py-1 rounded-md font-bold border-2 transition ${
                        isNcBill
                          ? 'bg-amber-500 text-white border-amber-500 hover:bg-amber-600'
                          : 'bg-amber-50 text-amber-700 border-amber-300 hover:bg-amber-100'
                      }`}
                    >
                      {isNcBill ? 'NC Bill ✓' : 'NC Bill'}
                    </button>
                  )}
                </span>
                <span>Qty</span>
                <span className="text-right">Amount</span>
              </div>
              {/* NC Bill reason dropdown */}
              {isNcBill && (
                <div className="mb-2">
                  <select
                    value={ncReason}
                    onChange={(e) => setNcReason(e.target.value)}
                    className="w-full text-[10px] px-2 py-1.5 border border-amber-300 rounded bg-amber-50 text-slate-700 focus:outline-none focus:ring-1 focus:ring-amber-400"
                  >
                    <option value="">Select NC reason...</option>
                    {NC_REASONS.map(r => <option key={r.code} value={r.code}>{r.label}</option>)}
                  </select>
                </div>
              )}
              {groupedItems.map((item) => {
                const isComped = !!compItems[item.groupKey];
                return (
                  <div key={item.groupKey} className={`mb-2 ${isComped ? 'bg-amber-50 rounded px-1 py-0.5' : ''}`}>
                    <div className="flex justify-between text-xs text-slate-900 items-center">
                      <span className="flex-1 flex items-center gap-1">
                        {item.name}
                        {item.variantName && <span className="text-slate-500">({item.variantName})</span>}
                        {isComped && (
                          <span className="text-[9px] font-bold bg-amber-500 text-white px-1 py-0.5 rounded">NC</span>
                        )}
                      </span>
                      <span className="w-8 text-center">{item.quantity}</span>
                      <span className={`w-20 text-right font-semibold ${isComped ? 'line-through text-slate-400' : ''}`}>
                        ₹{(item.price * item.quantity).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="text-xs text-slate-600 pl-1">@ ₹{item.price}</div>
                      {canMarkNc && (
                        <button
                          type="button"
                          onClick={() => !isNcBill && toggleComp(item.groupKey)}
                          disabled={isNcBill}
                          className={`text-[10px] px-1.5 py-0.5 rounded transition ${
                            isComped
                              ? 'bg-amber-500 text-white hover:bg-amber-600'
                              : 'text-slate-400 hover:text-amber-600 hover:bg-amber-50 border border-slate-200'
                          } ${isNcBill ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          <Gift size={10} className="inline mr-0.5" />
                          {isNcBill ? 'NC' : isComped ? 'Undo NC' : 'NC'}
                        </button>
                      )}
                    </div>
                    {isComped && !isNcBill && (
                      <select
                        value={compItems[item.groupKey]?.reason || NC_REASONS[0].code}
                        onChange={(e) => setCompReason(item.groupKey, e.target.value)}
                        className="mt-1 w-full text-[10px] px-1.5 py-1 border border-amber-300 rounded bg-white text-amber-800 focus:outline-none focus:ring-1 focus:ring-amber-400"
                      >
                        <option value="">Select NC reason...</option>
                        {NC_REASONS.map((r) => (
                          <option key={r.code} value={r.code}>{r.label}</option>
                        ))}
                      </select>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Totals Section */}
            <div className="border-t border-slate-300 pt-2 space-y-1 text-xs">
              {receiptConfig.showSubtotal && (
                <div className="flex justify-between">
                  <span>Subtotal:</span>
                  <span>₹{subtotal.toFixed(2)}</span>
                </div>
              )}
              {promoDiscount > 0 && (
                <div className="flex justify-between text-green-600">
                  <span>
                    Discount {appliedDiscount?.code
                      ? `(${appliedDiscount.code})`
                      : discountType === 'PERCENTAGE'
                        ? `(${discountPercentage}%)`
                        : `(₹${discountFixedAmount.toFixed(2)})`}:
                  </span>
                  <span>- ₹{promoDiscount.toFixed(2)}</span>
                </div>
              )}
              {compAmount > 0 && (
                <div className="flex justify-between text-amber-600">
                  <span>{isNcBill ? 'NC Deduction (all items):' : `NC (${Object.keys(compItems).length} items):`}</span>
                  <span>- ₹{compAmount.toFixed(2)}</span>
                </div>
              )}
              {(CGST_RATE > 0 || SGST_RATE > 0) && (
                <>
                  <div className="flex justify-between">
                    <span>Taxable Amount:</span>
                    <span>₹{taxableAmount.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>CGST ({CGST_RATE.toFixed(1)}%):</span>
                    <span>₹{cgstAmount.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>SGST ({SGST_RATE.toFixed(1)}%):</span>
                    <span>₹{sgstAmount.toFixed(2)}</span>
                  </div>
                  {IS_INCLUSIVE_TAX && (
                    <div className="text-[10px] text-slate-500 italic text-center">
                      (Tax included in price)
                    </div>
                  )}
                </>
              )}
              <div className="flex justify-between">
                <span>Round Off:</span>
                <span>{roundOff >= 0 ? '+' : ''}₹{roundOff.toFixed(2)}</span>
              </div>
            </div>

            {/* Final Amount */}
            {receiptConfig.showGrandTotal && (
              <div className="border-t-2 border-dashed border-slate-300 pt-3 mt-2">
                <div className={`flex justify-between text-sm font-bold p-2 rounded border ${
                  isNcBill ? 'bg-amber-50 border-amber-200' : 'bg-teal-50 border-teal-200'
                }`}>
                  <span>{isNcBill ? 'Amount (NC):' : 'Final Amount:'}</span>
                  <span className={isNcBill ? 'text-amber-700' : 'text-teal-700'}>₹{finalAmount.toFixed(2)}</span>
                </div>
                {isNcBill && finalAmount === 0 && (
                  <div className="text-center mt-2 text-xs font-semibold text-amber-600">
                    NO PAYMENT REQUIRED — NON-CHARGEABLE
                  </div>
                )}
              </div>
            )}

            {/* QR Code Section */}
            {receiptConfig.showQrCode && (
              <div className="border-t border-dashed border-slate-300 mt-3 pt-3 text-center">
                <p className="text-xs font-semibold text-slate-600 mb-2">SCAN TO PAY</p>
                {(receiptConfig.qrCodeData || branchDetails?.upiId) ? (
                  <div className="bg-slate-100 p-3 rounded-lg inline-block">
                    <div className="w-24 h-24 bg-white border-2 border-slate-300 rounded flex items-center justify-center mx-auto mb-2">
                      <span className="text-[10px] text-slate-400 text-center">QR Code<br/>on print</span>
                    </div>
                    <p className="text-xs text-slate-600">UPI: {receiptConfig.qrCodeData || branchDetails.upiId}</p>
                  </div>
                ) : (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <p className="text-xs text-amber-700 font-medium">[QR Code Not Available]</p>
                    <p className="text-[10px] text-amber-600 mt-1">Configure UPI ID in Receipt Settings</p>
                  </div>
                )}
              </div>
            )}

            {/* Footer */}
            {receiptConfig.showThankYou && (
              <div className="text-center border-t border-dashed border-slate-300 mt-3 pt-3">
                <p className="text-xs font-semibold text-slate-700">
                  {receiptConfig.thankYouText || branchDetails?.thankYouMessage || 'Thank you! Visit Again!'}
                </p>
              </div>
            )}
            {receiptConfig.footerText && (
              <p className="text-xs text-slate-500 text-center mt-1">{receiptConfig.footerText}</p>
            )}
          </div>
        </div>

        {/* Footer - Action Buttons */}
        <div className="bg-slate-50 border-t border-slate-200 p-4 flex-shrink-0">
          {/* Split Bill Result Banner */}
          {splitResult && (
            <div className="mb-3 p-3 bg-violet-50 border border-violet-200 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-bold text-violet-700 uppercase tracking-wide">Bill Split ({splitResult.length} people)</span>
                <button onClick={() => setSplitResult(null)} className="text-xs text-violet-500 hover:text-violet-700 underline">Clear</button>
              </div>
              <div className="space-y-1">
                {splitResult.map((s, i) => (
                  <div key={i} className="flex justify-between text-xs text-violet-700">
                    <span className="font-medium">{s.label}</span>
                    <span className="font-bold">₹{s.amount.toFixed(2)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex gap-2">
            {/* Split Bill Button */}
            {!splitResult && receiptItems.length > 1 && (
              <button
                onClick={() => setShowSplitBill(true)}
                disabled={isPrinting}
                className="px-4 py-3 rounded-xl font-semibold text-sm bg-violet-100 text-violet-700 border border-violet-300 hover:bg-violet-200 transition flex items-center gap-1.5 disabled:opacity-50"
              >
                <Split size={16} />
                Split
              </button>
            )}

            {/* Print / Send Button */}
            <button
              onClick={handlePrint}
              disabled={isPrintDisabled}
              title={exceedsDiscountThreshold ? `Discount exceeds allowed limit (${discountThreshold}%)` : undefined}
              className={`flex-1 py-3 rounded-xl font-bold text-base transition-all flex items-center justify-center gap-2 ${
                isPrintDisabled
                  ? 'bg-slate-300 text-slate-500 cursor-not-allowed'
                  : `bg-gradient-to-r ${themeColors.gradient} hover:${themeColors.hoverGradient} text-white shadow-lg hover:shadow-xl`
              }`}
            >
              {isPrinting ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                  {printingStatus === 'creating_bill' ? 'Generating Bill...' :
                    printingStatus === 'sending' ? 'Sending e-Bill...' :
                    mode === 'ebill' ? 'Sending...' : 'Printing Receipt...'}
                </>
              ) : (
                <>
                  {mode === 'ebill' ? <Send size={18} /> : <Printer size={18} />}
                  {splitResult ? `Print ${splitResult.length} Receipts` : (mode === 'ebill' ? 'Send e-Bill' : 'Print Receipt')}
                </>
              )}
            </button>
          </div>
        </div>

        {/* Split Bill Modal */}
        {showSplitBill && (
          <SplitBillModal
            items={receiptItems.map(item => ({
              ...item,
              menuItemId: groupedItems.find(g => g.name + (g.variantName ? ` (${g.variantName})` : '') === item.name)?.menuItemId,
              variantId: groupedItems.find(g => g.name + (g.variantName ? ` (${g.variantName})` : '') === item.name)?.variantId,
            }))}
            finalAmount={finalAmount}
            taxAmount={taxAmount}
            taxPercentage={TAX_PERCENTAGE}
            cgstRate={CGST_RATE}
            sgstRate={SGST_RATE}
            onClose={() => setShowSplitBill(false)}
            onConfirm={(splits) => {
              setSplitResult(splits);
              setShowSplitBill(false);
            }}
          />
        )}
      </div>

      {/* Discount Selection Modal */}
      {canApplyPromo && (
        <DiscountSelectionModal
          isOpen={showDiscountModal}
          onClose={() => setShowDiscountModal(false)}
          suggestions={discountSuggestions}
          loading={discountsLoading}
          onSelectDiscount={(discount) => {
            applyDiscount(discount);
            toast.success(`${discount.code} applied!`);
          }}
          onValidateCode={async (code) => {
            return await validateCode(code, {
              orderType: tableInfo?.tableType === 'TABLE' ? 'dine_in' : 'takeaway',
              subtotal,
              itemIds: [...new Set(allKOTItems.map(item => item.menuItemId).filter(Boolean))],
              categoryIds: [],
              customerId: null,
            });
          }}
          validating={codeValidating}
          calculateSaving={(discount) => {
            if (discount.eligible === false) return 0;
            const cartItems = groupedItems.map(item => ({
              menuItemId: item.menuItemId,
              categoryId: item.categoryId,
              price: item.finalPrice || item.price,
              quantity: item.quantity,
            }));
            return calculateDiscountAmount(discount, cartItems, subtotal);
          }}
        />
      )}
    </div>
  );
}